import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { PlusCircle, CheckCircle, ExternalLink, ListPlus } from 'lucide-react'
import api from '../../services/api'
import type { ResourceType } from '../../types'
import AddToListModal from '../../components/lists/AddToListModal'

interface ManualResourceForm {
  url: string
  title: string
  resource_type: ResourceType
  description: string
  creator: string
  topics: string
}

const RESOURCE_TYPES: { value: ResourceType; label: string }[] = [
  { value: 'journal', label: 'Journal Article' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'image', label: 'Image' },
  { value: 'document', label: 'Document' },
  { value: 'website', label: 'Website' },
  { value: 'course', label: 'Course' },
]

function isValidUrl(str: string): boolean {
  try {
    new URL(str)
    return true
  } catch {
    return false
  }
}

export default function ManualAdd() {
  const navigate = useNavigate()
  const [form, setForm] = useState<ManualResourceForm>({
    url: '',
    title: '',
    resource_type: 'website',
    description: '',
    creator: '',
    topics: '',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof ManualResourceForm, string>>>({})
  const [createdResourceId, setCreatedResourceId] = useState<string | null>(null)
  const [showAddToList, setShowAddToList] = useState(false)

  const submitMutation = useMutation({
    mutationFn: async (data: ManualResourceForm) => {
      const topicsList = data.topics
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)

      const response = await api.post('/academic/resources', {
        source_url: data.url,
        title: data.title,
        resource_type: data.resource_type,
        description: data.description || undefined,
        creator: data.creator || undefined,
        topics: topicsList.length > 0 ? topicsList : undefined,
      })
      return response.data
    },
    onSuccess: (data) => {
      setCreatedResourceId(data.resource_id || data.id || null)
    },
  })

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ManualResourceForm, string>> = {}

    if (!form.title.trim()) {
      newErrors.title = 'Title is required'
    }
    if (!form.url.trim()) {
      newErrors.url = 'URL is required'
    } else if (!isValidUrl(form.url)) {
      newErrors.url = 'Please enter a valid URL (e.g., https://example.com)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      submitMutation.mutate(form)
    }
  }

  const handleChange = (field: keyof ManualResourceForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  // Success state
  if (submitMutation.isSuccess) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center py-16">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Resource Added Successfully</h2>
          <p className="text-gray-500 mb-6">
            Your resource &ldquo;{form.title}&rdquo; has been added to the system.
          </p>
          <div className="flex items-center justify-center gap-3">
            {createdResourceId && (
              <button
                onClick={() => setShowAddToList(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-acad-300 px-4 py-2 text-sm font-medium text-acad-600 hover:bg-acad-50 transition-colors"
              >
                <ListPlus className="h-4 w-4" />
                Add to a List
              </button>
            )}
            <button
              onClick={() => {
                setForm({
                  url: '',
                  title: '',
                  resource_type: 'website',
                  description: '',
                  creator: '',
                  topics: '',
                })
                submitMutation.reset()
                setCreatedResourceId(null)
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 transition-colors"
            >
              <PlusCircle className="h-4 w-4" />
              Add Another Resource
            </button>
          </div>
        </div>

        {showAddToList && createdResourceId && (
          <AddToListModal
            isOpen={showAddToList}
            onClose={() => setShowAddToList(false)}
            resourceId={createdResourceId}
            resourceType={form.resource_type}
            title={form.title}
          />
        )}
      </div>
    )
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Add Resource</h1>
        <p className="text-gray-500 mt-1">
          Manually add a resource link to your academic library
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="space-y-5">
          {/* URL */}
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              URL <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                id="url"
                type="url"
                value={form.url}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://example.com/resource"
                className={`w-full rounded-lg border pl-10 pr-3 py-2 text-sm outline-none transition-colors ${
                  errors.url
                    ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                    : 'border-gray-300 focus:border-acad-500 focus:ring-1 focus:ring-acad-500'
                }`}
              />
            </div>
            {errors.url && <p className="mt-1 text-xs text-red-500">{errors.url}</p>}
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Resource title"
              className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
                errors.title
                  ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                  : 'border-gray-300 focus:border-acad-500 focus:ring-1 focus:ring-acad-500'
              }`}
            />
            {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
          </div>

          {/* Resource Type */}
          <div>
            <label htmlFor="resource_type" className="block text-sm font-medium text-gray-700 mb-1">
              Resource Type
            </label>
            <select
              id="resource_type"
              value={form.resource_type}
              onChange={(e) => handleChange('resource_type', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-acad-500 focus:ring-1 focus:ring-acad-500 bg-white"
            >
              {RESOURCE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of the resource"
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-acad-500 focus:ring-1 focus:ring-acad-500 resize-none"
            />
          </div>

          {/* Authors/Creator */}
          <div>
            <label htmlFor="creator" className="block text-sm font-medium text-gray-700 mb-1">
              Authors / Creator
            </label>
            <input
              id="creator"
              type="text"
              value={form.creator}
              onChange={(e) => handleChange('creator', e.target.value)}
              placeholder="Author or creator name"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-acad-500 focus:ring-1 focus:ring-acad-500"
            />
          </div>

          {/* Topics */}
          <div>
            <label htmlFor="topics" className="block text-sm font-medium text-gray-700 mb-1">
              Subject / Topic Tags
            </label>
            <input
              id="topics"
              type="text"
              value={form.topics}
              onChange={(e) => handleChange('topics', e.target.value)}
              placeholder="e.g., machine learning, climate science, pedagogy"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-acad-500 focus:ring-1 focus:ring-acad-500"
            />
            <p className="mt-1 text-xs text-gray-400">Separate multiple tags with commas</p>
          </div>
        </div>

        {/* Error message */}
        {submitMutation.isError && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            Failed to add resource. Please try again.
          </div>
        )}

        {/* Submit */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitMutation.isPending}
            className="rounded-lg bg-acad-500 px-5 py-2 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitMutation.isPending ? 'Adding...' : 'Add Resource'}
          </button>
        </div>
      </form>
    </div>
  )
}
